import { HotelsList } from '@/components/hotels';
import { hotelCrudConfig, type Hotel } from '@/config/objects/hotels';
import { type PromotionalOffer } from '@/config/objects/promotional-offers';
import { useApiClient, type CrudConfig } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

export function PromotionalOfferHotelsTab() {
  const { id } = useParams<{ id: string }>();
  const apiClient = useApiClient();

  const { data: offerData } = useQuery<{ success: boolean; data: PromotionalOffer }>({
    queryKey: ['PromotionalOffer', id],
    queryFn: () => apiClient.get(`/promotional-offers/${id}`),
    enabled: !!id,
  });

  const promotionalOffer = offerData?.data;

  const config: CrudConfig<Hotel> = useMemo(
    () => ({
      ...hotelCrudConfig,
      entityName: 'Hotel',
      entityNamePlural: 'Hotels',
      endpoints: {
        ...hotelCrudConfig.endpoints,
        list: `/promotional-offers/${id}/hotels`,
        delete: (hotelId: string) => `/promotional-offers/${id}/hotels/${hotelId}`,
      },
      listView: {
        ...hotelCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/hotels',
            where: promotionalOffer
              ? {
                  promotionalOffers: {
                    none: {
                      promotionalOfferId: promotionalOffer.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (hotel: Hotel) => {
              await apiClient.post(`/promotional-offers/${id}/hotels`, { hotelId: hotel.id });
              toast.success('Hotel added to promotional offer');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (hotel: Hotel) => {
              await apiClient.delete(`/promotional-offers/${id}/hotels/${hotel.id}`);
              toast.success('Hotel removed from promotional offer');
            },
          },
        },
      },
    }),
    [promotionalOffer, id, apiClient],
  );

  if (!id) return null;

  return <HotelsList config={config} className="p-0" baseUrl="/hotels" />;
}
